/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getStockStatusAsync } from '../actions/DataActionExtension.g';
import { generateCacheKey, getSelectedProductIdFromActionInput } from '@msdyn365-commerce-modules/retail-actions';

/**
 * GetStockStatus Input Action
 */
export class GetStockStatusInput implements Msdyn365.IActionInput {
    public productId: number;
    public apiSettings: Msdyn365.ICommerceApiSettings;
    constructor(productId: number, apiSettings: Msdyn365.ICommerceApiSettings) {
        this.productId = productId;
        this.apiSettings = apiSettings;
    }
    public getCacheKey = () => generateCacheKey(this.apiSettings, { key: 'productId', value: `${this.productId}` });
    public getCacheObjectType = () => 'stockStatus';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

export interface IGetStockStatusData {
    stockStatus: string;
}

const createStockStatusInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const context = args.requestContext;
    const appSettings = context.apiSettings;
    const productId = getSelectedProductIdFromActionInput(args);
    if (productId) {
        return new GetStockStatusInput(parseInt(productId, 10), appSettings);
    }
    throw Error('Unable to create GetStockStatusInput, no productId found in the URL');
};

async function action(input: GetStockStatusInput, ctx: Msdyn365.IActionContext): Promise<IGetStockStatusData> {
    const res = await getStockStatusAsync({ callerContext: ctx }, input.productId);
    if (res) {
        return { stockStatus: res.StockStatus || '' };
    }

    throw new Error('Unable to get product');
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetStockStatusData>>action,
    id: 'GetStockStatus',
    input: createStockStatusInput
});
